import React, { useState, useEffect } from 'react';
import { X, ArrowLeftRight, Package, DollarSign, MessageSquare, Loader2, Check, ChevronDown, Image as ImageIcon } from 'lucide-react';
import axios from '../lib/axios';
import { useAuth, API } from '../App';
import { toast } from '../utils/toastWithSound';
import { safeClick } from '../utils/safeClick';

const ProposeTradeModal = ({ listing, onClose, onSuccess }) => {
  const { user } = useAuth();
  const [step, setStep] = useState(1); // 1: Choose offer type, 2: Details, 3: Review
  const [loading, setLoading] = useState(false);
  const [userListings, setUserListings] = useState([]);
  const [loadingListings, setLoadingListings] = useState(false);
  
  // Form state
  const [offerType, setOfferType] = useState(null); // 'listing', 'items', 'mixed'
  const [selectedListing, setSelectedListing] = useState(null);
  const [itemsDescription, setItemsDescription] = useState('');
  const [offeredValue, setOfferedValue] = useState('');
  const [message, setMessage] = useState('');
  const [showListingDropdown, setShowListingDropdown] = useState(false);

  // Body scroll lock when modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  // Fetch user's listings for trade
  useEffect(() => {
    const fetchUserListings = async () => {
      if (!user) return;
      setLoadingListings(true);
      try {
        const response = await axios.get(`${API}/listings/my`);
        // Filter to only show active listings
        const activeListings = (response.data.listings || response.data || [])
          .filter(l => l.status === 'active' && l.id !== listing.id);
        setUserListings(activeListings);
      } catch (error) {
        console.error('Failed to fetch user listings:', error);
      } finally {
        setLoadingListings(false);
      }
    };
    fetchUserListings();
  }, [user, listing.id]);

  const handleSubmit = async () => {
    if (!offerType) {
      toast.error('Please select what you want to offer');
      return;
    }
    
    if (offerType === 'listing' && !selectedListing) {
      toast.error('Please select a listing to offer');
      return;
    }
    
    if ((offerType === 'items' || offerType === 'mixed') && !itemsDescription.trim()) {
      toast.error('Please describe what you\'re offering');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        listing_id: listing.id,
        offered_listing_id: selectedListing?.id || null,
        offered_items_description: itemsDescription.trim() || null,
        offered_value: offeredValue ? parseFloat(offeredValue) : null,
        message: message.trim() || null
      };

      await axios.post(`${API}/trade-proposals`, payload);

      toast.success('Trade proposal sent!');
      onSuccess?.();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send trade proposal');
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price, currency = 'USD') => {
    if (price === null || price === undefined || isNaN(price)) {
      return 'Price not set';
    }
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(price);
  };

  // Helper to get price from listing (handles different price structures)
  const getListingPrice = (listing) => {
    if (listing.price !== null && listing.price !== undefined) {
      return listing.price;
    }
    if (listing.pricing?.amount !== null && listing.pricing?.amount !== undefined) {
      return listing.pricing.amount;
    }
    return null;
  };

  const getListingCurrency = (listing) => {
    return listing.currency || listing.pricing?.currency || 'USD';
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[150] flex items-center justify-center p-4" onClick={onClose}>
      <div 
        className="bg-card rounded-2xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-4 border-b border-border flex items-center justify-between bg-gradient-to-r from-emerald-500/10 to-teal-500/10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
              <ArrowLeftRight className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="font-bold text-foreground">Propose a Trade</h2>
              <p className="text-xs text-muted-foreground">for "{listing.title}"</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-secondary rounded-full transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {/* Step 1: Choose offer type */}
          {step === 1 && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground mb-4">
                What would you like to offer in exchange?
              </p>
              
              <button
                onClick={() => { setOfferType('listing'); setStep(2); }}
                className={`w-full p-4 rounded-xl border-2 text-left transition-all hover:border-emerald-500/50 hover:bg-emerald-500/5 ${
                  offerType === 'listing' ? 'border-emerald-500 bg-emerald-500/10' : 'border-border'
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                    <Package className="w-5 h-5 text-emerald-500" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground">One of My Listings</h3>
                    <p className="text-sm text-muted-foreground">Trade one of your active listings</p>
                  </div>
                </div>
              </button>

              <button
                onClick={() => { setOfferType('items'); setStep(2); }}
                className={`w-full p-4 rounded-xl border-2 text-left transition-all hover:border-blue-500/50 hover:bg-blue-500/5 ${
                  offerType === 'items' ? 'border-blue-500 bg-blue-500/10' : 'border-border'
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center flex-shrink-0">
                    <MessageSquare className="w-5 h-5 text-blue-500" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground">Describe Items</h3>
                    <p className="text-sm text-muted-foreground">Describe items not listed on the marketplace</p>
                  </div>
                </div>
              </button>

              <button
                onClick={() => { setOfferType('mixed'); setStep(2); }}
                className={`w-full p-4 rounded-xl border-2 text-left transition-all hover:border-purple-500/50 hover:bg-purple-500/5 ${
                  offerType === 'mixed' ? 'border-purple-500 bg-purple-500/10' : 'border-border'
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center flex-shrink-0">
                    <DollarSign className="w-5 h-5 text-purple-500" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground">Listing + Cash/Items</h3>
                    <p className="text-sm text-muted-foreground">Combine a listing with additional items or cash</p>
                  </div>
                </div>
              </button>
            </div>
          )}

          {/* Step 2: Details */}
          {step === 2 && (
            <div className="space-y-4">
              <button 
                onClick={() => setStep(1)}
                className="text-sm text-primary hover:underline flex items-center gap-1"
              >
                ← Back to offer types
              </button>

              {/* Select listing (for 'listing' or 'mixed') */}
              {(offerType === 'listing' || offerType === 'mixed') && (
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Select a listing to offer
                  </label>
                  
                  {loadingListings ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-primary" />
                    </div>
                  ) : userListings.length === 0 ? (
                    <div className="text-center py-6 bg-secondary/50 rounded-xl">
                      <Package className="w-10 h-10 mx-auto mb-2 text-muted-foreground/50" />
                      <p className="text-sm text-muted-foreground">You don't have any active listings to trade</p>
                    </div>
                  ) : (
                    <div className="relative">
                      <button
                        {...safeClick(() => setShowListingDropdown(!showListingDropdown))}
                        className="w-full p-3 bg-secondary border border-border rounded-xl flex items-center justify-between hover:border-primary/50 transition-colors touch-manipulation"
                      >
                        {selectedListing ? (
                          <div className="flex items-center gap-3">
                            <div className="w-12 h-12 rounded-lg overflow-hidden bg-secondary flex-shrink-0">
                              {selectedListing.images?.[0] ? (
                                <img src={selectedListing.images[0]} alt="" className="w-full h-full object-cover" />
                              ) : (
                                <ImageIcon className="w-6 h-6 m-3 text-muted-foreground" />
                              )}
                            </div>
                            <div className="text-left">
                              <p className="font-medium text-foreground line-clamp-1">{selectedListing.title}</p>
                              <p className="text-sm text-muted-foreground">{formatPrice(getListingPrice(selectedListing), getListingCurrency(selectedListing))}</p>
                            </div>
                          </div>
                        ) : (
                          <span className="text-muted-foreground">Choose a listing...</span>
                        )}
                        <ChevronDown className={`w-5 h-5 text-muted-foreground transition-transform ${showListingDropdown ? 'rotate-180' : ''}`} />
                      </button>
                      
                      {showListingDropdown && (
                        <div className="absolute top-full left-0 right-0 mt-2 bg-card border border-border rounded-xl shadow-xl z-10 max-h-60 overflow-y-auto">
                          {userListings.map(item => (
                            <button
                              key={item.id}
                              onClick={() => {
                                setSelectedListing(item);
                                setShowListingDropdown(false);
                              }}
                              className={`w-full p-3 flex items-center gap-3 hover:bg-secondary transition-colors ${
                                selectedListing?.id === item.id ? 'bg-primary/10' : ''
                              }`}
                            >
                              <div className="w-12 h-12 rounded-lg overflow-hidden bg-secondary flex-shrink-0">
                                {item.images?.[0] ? (
                                  <img src={item.images[0]} alt="" className="w-full h-full object-cover" />
                                ) : (
                                  <ImageIcon className="w-6 h-6 m-3 text-muted-foreground" />
                                )}
                              </div>
                              <div className="text-left flex-1">
                                <p className="font-medium text-foreground line-clamp-1">{item.title}</p>
                                <p className="text-sm text-muted-foreground">{formatPrice(getListingPrice(item), getListingCurrency(item))}</p>
                              </div>
                              {selectedListing?.id === item.id && (
                                <Check className="w-5 h-5 text-primary" />
                              )}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Items description (for 'items' or 'mixed') */}
              {(offerType === 'items' || offerType === 'mixed') && (
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    {offerType === 'mixed' ? 'Additional items or cash to include' : 'Describe what you\'re offering'}
                  </label>
                  <textarea
                    value={itemsDescription}
                    onChange={(e) => setItemsDescription(e.target.value)}
                    placeholder={offerType === 'mixed' 
                      ? "e.g., Plus $200 cash, or plus a MacBook charger..."
                      : "e.g., iPhone 14 Pro, excellent condition with original box..."
                    }
                    className="w-full p-3 bg-secondary border border-border rounded-xl resize-none h-24 focus:outline-none focus:ring-2 focus:ring-primary/50"
                  />
                </div>
              )}

              {/* Estimated value */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Estimated value of your offer (optional)
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                  <input
                    type="number"
                    value={offeredValue}
                    onChange={(e) => setOfferedValue(e.target.value)}
                    placeholder="0.00"
                    className="w-full pl-8 pr-4 py-3 bg-secondary border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/50"
                  />
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  The listing is valued at {formatPrice(getListingPrice(listing), getListingCurrency(listing))}
                </p>
              </div>

              {/* Message */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Message to seller (optional)
                </label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Tell them why you want to trade, or ask any questions..."
                  className="w-full p-3 bg-secondary border border-border rounded-xl resize-none h-20 focus:outline-none focus:ring-2 focus:ring-primary/50"
                />
              </div>

              <button
                onClick={() => setStep(3)}
                disabled={(offerType === 'listing' && !selectedListing) || ((offerType === 'items' || offerType === 'mixed') && !itemsDescription.trim())}
                className="w-full py-3 bg-primary text-white rounded-xl font-medium hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Review Proposal
              </button>
            </div>
          )}

          {/* Step 3: Review */}
          {step === 3 && (
            <div className="space-y-4">
              <button 
                onClick={() => setStep(2)}
                className="text-sm text-primary hover:underline flex items-center gap-1"
              >
                ← Back to details
              </button>

              <div className="bg-secondary/50 rounded-xl p-4 space-y-4">
                <h3 className="font-semibold text-foreground">Review Your Trade Proposal</h3>
                
                {/* What you want */}
                <div className="flex items-center gap-3 p-3 bg-card rounded-lg border border-border">
                  <div className="w-16 h-16 rounded-lg overflow-hidden bg-secondary flex-shrink-0">
                    {listing.images?.[0] ? (
                      <img src={listing.images[0]} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <ImageIcon className="w-8 h-8 m-4 text-muted-foreground" />
                    )}
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wider">You want</p>
                    <p className="font-medium text-foreground">{listing.title}</p>
                    <p className="text-sm text-primary font-semibold">{formatPrice(getListingPrice(listing), getListingCurrency(listing))}</p>
                  </div>
                </div>

                <div className="flex items-center justify-center">
                  <ArrowLeftRight className="w-6 h-6 text-muted-foreground" />
                </div>

                {/* What you're offering */}
                <div className="p-3 bg-card rounded-lg border border-emerald-500/30">
                  <p className="text-xs text-emerald-500 uppercase tracking-wider mb-2">You're offering</p>
                  
                  {selectedListing && (
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-12 h-12 rounded-lg overflow-hidden bg-secondary flex-shrink-0">
                        {selectedListing.images?.[0] ? (
                          <img src={selectedListing.images[0]} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <ImageIcon className="w-6 h-6 m-3 text-muted-foreground" />
                        )}
                      </div>
                      <div>
                        <p className="font-medium text-foreground">{selectedListing.title}</p>
                        <p className="text-sm text-emerald-500">{formatPrice(getListingPrice(selectedListing), getListingCurrency(selectedListing))}</p>
                      </div>
                    </div>
                  )}
                  
                  {itemsDescription && (
                    <p className="text-sm text-foreground">
                      {selectedListing ? '+ ' : ''}{itemsDescription}
                    </p>
                  )}
                  
                  {offeredValue && parseFloat(offeredValue) > 0 && (
                    <p className="text-sm text-muted-foreground mt-1">
                      Estimated value: {formatPrice(parseFloat(offeredValue))}
                    </p>
                  )}
                </div>

                {message && (
                  <div className="p-3 bg-card rounded-lg border border-border">
                    <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Your message</p>
                    <p className="text-sm text-foreground">{message}</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        {step === 3 && (
          <div className="p-4 border-t border-border bg-secondary/30">
            <button
              onClick={handleSubmit}
              disabled={loading}
              className="w-full py-3 bg-gradient-to-r from-emerald-500 to-teal-600 text-white rounded-xl font-semibold hover:from-emerald-600 hover:to-teal-700 transition-all disabled:opacity-50 flex items-center justify-center gap-2 shadow-lg shadow-emerald-500/20"
            >
              {loading ? (
                <><Loader2 className="w-5 h-5 animate-spin" /> Sending...</>
              ) : (
                <><ArrowLeftRight className="w-5 h-5" /> Send Trade Proposal</>
              )}
            </button>
            <p className="text-xs text-center text-muted-foreground mt-2">
              The seller will be notified and can accept, reject, or counter your offer
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProposeTradeModal;
